// KPIs sidebar (KPIS-MODE, founder-approved 2026-10-22: Chart and Cadence as one mode). Top rows:
// Chart (the org/accountability chart), Scorecard (the EOS grid; Published is its tab), My
// numbers (with the count still due as a pill; My scorecard is its tab), Priorities, Issues,
// Meetings, Vision — each only for a viewer its page's gate lets in (`chart`: every member;
// `cadence`: the KPI readers, a plain member its own pages). DATA ENTRY (managers, footer):
// Enter hours, Enter numbers, Import CSV. No WEEKS list: the top bar's period picker sets the
// period.
// UX-A (ux pass 1.3, founder-approved): the TEAMS and SEATS trees became one SCOPE tree —
// Whole company › team › seat (`kpiScopeTree`). The Whole company and team rows set the scope
// (`scope=team:{id}`) on the chart or the scorecard; a seat row opens that seat's scorecard (the
// grid filtered to it). Seat nodes are the seat title over its holder(s) with a DE or person
// badge ("Open" when none; WORKFORCE 2026-10-26), the agents (AG) reporting to a holder hung
// under the seat, and a hover "Open DE" when a DE holds it. Nodes expand and collapse, remembered
// per company; seats are cut to those whose KPIs the viewer may see — the server's
// `kpi_visible_seat_ids` (`GET org-chart/kpi-visibility`), never a client rule.
import {
  AvatarTile,
  CountPill,
  NavList,
  NavRow,
  SidebarCaption,
  SidebarEmpty,
  SidebarPanel,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import {
  BotIcon,
  Building2Icon,
  CalendarClockIcon,
  ChevronRightIcon,
  CircleDotIcon,
  CompassIcon,
  FileTextIcon,
  HashIcon,
  NetworkIcon,
  Rows3Icon,
  SparklesIcon,
  TargetIcon,
  UsersIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import type { WorkforceAgentRow } from "../../api/types";
import { canOpen } from "../../extensions/modes";
import type { SidebarProps } from "../../extensions/registry";
import type { SeatNode } from "../../features/chart/chart-model";
import { kpiVisibilityQuery } from "../../features/chart/chart-queries";
import { isManager } from "../../features/company";
import { seatHolderLabel } from "../../features/format";
import { holdersOf, myMembershipId, orgChartQuery } from "../../features/home/home-queries";
import { useAsk } from "../../features/insights/ask-host";
import { outstanding } from "../../features/numbers/my-numbers-model";
import { focusQuery } from "../../features/portfolio/portfolio-queries";
import { myNumbersQuery, workforceAgentsQuery } from "../../features/queries";
import { useSession } from "../../features/use-feature";
import { applyPatch, keepFocus, useFocus } from "../../focus/focus";
import { SwitcherTile } from "../company-switcher";
import { ManagerDataRows } from "./data-rows";
import { kpiScopeTree, readTreeOpen, type ScopeTeam, treeIds, writeTreeOpen } from "./kpis-tree";
import { ReportsSection } from "./reports";

const active = { includeSearch: false } as const;
/** The tree's indent per depth (4px + 12px a level; no inline style). */
const TREE_INDENT = [
  "pl-1",
  "pl-4",
  "pl-7",
  "pl-10",
  "pl-13",
  "pl-16",
  "pl-19",
  "pl-22",
  "pl-25",
] as const;
const indent = (depth: number) => TREE_INDENT[Math.min(depth, TREE_INDENT.length - 1)];
/** WORKFORCE: a holder's badge kind. */
const holderKind = (k: string) => (k === "digital_employee" ? "de" : "person") as "de" | "person";
/** The expand key of the Whole company row and of a team row (seat rows use the seat id). */
const COMPANY = "scope:company";
const teamKey = (id: string) => `team:${id}`;
/** One tree row's frame (the seat tree's look, kept from KPIS-MODE). */
const ROW =
  "group/nav-row relative mx-2 flex min-h-8 items-center gap-1 rounded-md pr-2 text-sm text-sidebar-foreground transition-colors hover:bg-muted hover:text-foreground pointer-coarse:min-h-11 data-[current=true]:bg-primary-soft data-[current=true]:font-medium data-[current=true]:text-primary-text";

export function KpisSidebar({ company, children }: SidebarProps) {
  const { me } = useSession();
  const ask = useAsk();
  const params = { slug: company.slug };
  const chart = canOpen("chart", me, company);
  const readers = canOpen("cadence", me, company);
  const mine = canOpen("cadence", me, company, true);
  const reports = canOpen("reports", me, company);
  // On a Reports page the sidebar lists Reports' own rows (weeks, Finance) under the KPIs rows.
  const inReports = useRouterState({
    select: (s) => /^\/c\/[^/]+\/kpis\/reports(\/|$)/.test(s.location.pathname),
  });
  const row = (to: string, label: string, icon: typeof NetworkIcon) => (
    <NavRow
      key={to}
      icon={icon}
      label={label}
      render={
        <Link
          to={to as never}
          params={params as never}
          search={keepFocus as never}
          activeOptions={active}
        />
      }
    />
  );
  return (
    <SidebarPanel
      title="KPIs"
      footer={
        isManager(company) ? (
          <>
            <SidebarCaption label="Data entry" />
            <ManagerDataRows company={company} />
          </>
        ) : null
      }
    >
      <NavList data-slot="kpis-nav">
        {chart ? row("/c/$slug/kpis/chart", "Chart", NetworkIcon) : null}
        {mine ? row("/c/$slug/kpis/scorecard", "Scorecard", Rows3Icon) : null}
        {mine ? <MyNumbersRow company={company} member={!readers} /> : null}
        {/* DE-KPIS-LIVE: every DE's runs, KPIs and cost (each row under its own record access). */}
        {chart || readers ? row("/c/$slug/kpis/agents", "DE KPIs", BotIcon) : null}
        {chart || readers ? (
          <>
            {row("/c/$slug/kpis/priorities", "Priorities", TargetIcon)}
            {row("/c/$slug/kpis/issues", "Issues", CircleDotIcon)}
            {row("/c/$slug/kpis/meetings", "Meetings", CalendarClockIcon)}
            {row("/c/$slug/kpis/vision", "Vision", CompassIcon)}
          </>
        ) : null}
        {/* NAV-REDESIGN: Reports moved here from the top bar (its readers, as its tab was). */}
        {reports ? (
          <NavRow
            icon={FileTextIcon}
            label="Reports"
            data-slot="kpis-reports"
            render={
              <Link
                to="/c/$slug/kpis/reports"
                params={params}
                search={keepFocus}
                activeOptions={{ includeSearch: false }}
              />
            }
          />
        ) : null}
        {/* ADMIN-INSIGHTS: Ask about the numbers (owners and admins; nothing for members). */}
        {ask ? (
          <NavRow
            icon={SparklesIcon}
            label="Ask"
            data-slot="kpis-ask"
            render={<button type="button" onClick={() => ask()} />}
          />
        ) : null}
      </NavList>
      {inReports ? (
        <>
          <SidebarCaption label="Reports" />
          <ReportsSection company={company} settings />
        </>
      ) : null}
      {chart && !inReports ? (
        company.kind === "portfolio" ? (
          <PortfolioScope company={company} />
        ) : (
          <ScopeTree company={company} />
        )
      ) : null}
      {children}
    </SidebarPanel>
  );
}

/**
 * FOUNDER-OS phase 1 (design §2.4): in Founder OS the scope reads Portfolio › company › CEO seat
 * (the companies are Focus's own query). Each row opens the roll-up grid at that company's row.
 */
function PortfolioScope({ company }: Pick<SidebarProps, "company">) {
  const { api } = useRouter().options.context;
  const focus = useQuery({ ...focusQuery(api, company.id), retry: false });
  const current = useRouterState({
    select: (s) =>
      /\/kpis\/scorecard$/.test(s.location.pathname)
        ? ((s.location.search as { company?: string }).company ?? "portfolio")
        : null,
  });
  const params = { slug: company.slug };
  const rows = [...(focus.data?.companies ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const link = (label: ReactNode, id: string | null, depth: number, icon?: ReactNode) => (
    <div
      data-current={current === (id ?? "portfolio") && depth < 2 ? "true" : undefined}
      className={`${indent(depth)} ${ROW}`}
    >
      <span aria-hidden="true" className="size-5 shrink-0" />
      <Link
        to="/c/$slug/kpis/scorecard"
        params={params}
        search={
          ((prev: Record<string, unknown>) => ({
            ...keepFocus(prev),
            ...(id ? { company: id } : {}),
          })) as never
        }
        className="flex min-w-0 flex-1 items-center gap-2 rounded-sm py-1 focus-visible:outline-2 focus-visible:outline-ring"
      >
        {icon}
        <span className="truncate">{label}</span>
      </Link>
    </div>
  );
  return (
    <>
      <SidebarCaption label="Scope" />
      <ul aria-label="Portfolio scope" data-slot="kpis-scope-tree" className="flex flex-col gap-px">
        <li data-scope="portfolio">
          {link(
            "Portfolio",
            null,
            0,
            <Building2Icon aria-hidden="true" className="size-4 shrink-0 text-faint" />,
          )}
          {rows.length ? (
            <ul className="flex flex-col gap-px">
              {rows.map((r) => (
                <li key={r.company_id} data-company={r.slug}>
                  {link(r.name, r.company_id, 1, <SwitcherTile name={r.name} portfolio={false} />)}
                  <ul className="flex flex-col gap-px">
                    <li>{link("CEO seat", r.company_id, 2)}</li>
                  </ul>
                </li>
              ))}
            </ul>
          ) : null}
        </li>
      </ul>
      {focus.isSuccess && rows.length === 0 ? <SidebarEmpty things="companies" /> : null}
    </>
  );
}

/** My numbers, with the count still late or due as a pill (a member's KPIs screens read it). */
function MyNumbersRow({ company, member }: Pick<SidebarProps, "company"> & { member: boolean }) {
  const { api } = useRouter().options.context;
  const numbers = useQuery({ ...myNumbersQuery(api, company.id), retry: false, enabled: member });
  const due = outstanding(numbers.data).length;
  return (
    <NavRow
      icon={HashIcon}
      label="My numbers"
      trailing={due ? <CountPill count={due} tone="accent" /> : undefined}
      render={
        <Link
          to="/c/$slug/kpis/my-numbers"
          params={{ slug: company.slug }}
          search={keepFocus}
          activeOptions={active}
          aria-label={due ? `My numbers, ${due} due` : "My numbers"}
        />
      }
    />
  );
}

/** The KPIs page a scope row lands on: the chart when it is open, else the scorecard. */
function useScopeTarget(): "/c/$slug/kpis/chart" | "/c/$slug/kpis/scorecard" {
  const onScorecard = useRouterState({
    select: (s) => /^\/c\/[^/]+\/kpis\/scorecard(\/|$)/.test(s.location.pathname),
  });
  return onScorecard ? "/c/$slug/kpis/scorecard" : "/c/$slug/kpis/chart";
}

/** The scope a row sets (null: the whole company), keeping the playhead and the page's keys. */
const scoped = (scope: string | null) => (prev: Record<string, unknown>) => {
  // The scope filters the chart and the grid: a Reports or Published tab gives way to the grid.
  const { scope: _drop, s: _s, p: _p, thread: _t, view: _v, ...rest } = prev;
  return { ...rest, ...applyPatch(keepFocus(prev), { scope }) };
};

type TreeSeat = {
  id: string;
  title: string;
  supervisor_seat_id: string | null;
  status?: string;
};

/** The chevron that opens or closes a node (a spacer when it has nothing under it). */
function Toggle({
  label,
  has,
  open,
  onToggle,
}: {
  label: string;
  has: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  if (!has) return <span aria-hidden="true" className="size-5 shrink-0" />;
  return (
    <button
      type="button"
      aria-label={`${open ? "Collapse" : "Expand"} ${label}`}
      aria-expanded={open}
      onClick={onToggle}
      className="flex size-5 shrink-0 items-center justify-center rounded-sm text-faint hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
    >
      <ChevronRightIcon
        aria-hidden="true"
        className={`size-3.5 transition-transform ${open ? "rotate-90" : ""}`}
      />
    </button>
  );
}

/**
 * SCOPE: Whole company › teams › seats. Each node toggles with its chevron (or ←/→ on the row);
 * the expanded set is remembered per company.
 */
function ScopeTree({ company }: Pick<SidebarProps, "company">) {
  const { api } = useRouter().options.context;
  const { me } = useSession();
  const navigate = useNavigate();
  const { focus } = useFocus();
  const to = useScopeTarget();
  const params = { slug: company.slug };
  const chart = useQuery({ ...orgChartQuery(api, company.id), retry: false });
  const visibility = useQuery({ ...kpiVisibilityQuery(api, company.id), retry: false });
  const holders = holdersOf(chart.data);
  // WORKFORCE: the agents (AG), by the membership they report to (shared cached read).
  const agents = useQuery({ ...workforceAgentsQuery(api, company.id), retry: false });
  const agsBy = useMemo(() => {
    const out = new Map<string, WorkforceAgentRow[]>();
    for (const a of agents.data ?? [])
      if (a.status !== "retired")
        out.set(a.reports_to_membership_id, [...(out.get(a.reports_to_membership_id) ?? []), a]);
    return out;
  }, [agents.data]);
  const mineId = myMembershipId(me, company);
  const tree = useMemo(
    () =>
      chart.data
        ? kpiScopeTree(
            chart.data.seats as TreeSeat[],
            new Set(visibility.data?.kpi_visible_seat_ids ?? []),
            chart.data.teams,
            chart.data.team_members,
          )
        : null,
    [chart.data, visibility.data],
  );
  const [open, setOpen] = useState<Set<string> | null>(null);
  // The remembered state, else Whole company, every team and the viewer's own seats open.
  useEffect(() => {
    if (open || !tree || visibility.isPending) return;
    const stored = readTreeOpen(company.id);
    if (stored) return setOpen(new Set(stored));
    const own = (ids: string[]) =>
      ids.filter((id) => (holders.get(id) ?? []).some((h) => h.membership_id === mineId));
    const next = new Set<string>([COMPANY, ...tree.rest.map((n) => n.seat.id)]);
    for (const id of own(treeIds(tree.rest))) next.add(id);
    // Every team open (the scope rows were all visible before UX-A), with its seat roots.
    const walk = (teams: ScopeTeam<TreeSeat>[]): void => {
      for (const t of teams) {
        next.add(teamKey(t.team.id));
        for (const id of own(treeIds(t.seats))) next.add(id);
        walk(t.teams);
      }
    };
    walk(tree.teams);
    setOpen(next);
  }, [tree, open, company.id, holders, mineId, visibility.isPending]);
  const toggle = (id: string, to?: boolean) => {
    const next = new Set(open ?? []);
    if (to ?? !next.has(id)) next.add(id);
    else next.delete(id);
    setOpen(next);
    writeTreeOpen(company.id, [...next]);
  };
  const keys = (id: string, has: boolean, expanded: boolean) => (e: { key: string }) => {
    if (e.key === "ArrowRight" && has && !expanded) toggle(id, true);
    if (e.key === "ArrowLeft" && has && expanded) toggle(id, false);
  };
  const focusedSeat = focus.subject?.kind === "seat" ? focus.subject.id : null;
  const onScorecard = useRouterState({
    select: (s) => /^\/c\/[^/]+\/kpis\/scorecard(\/|$)/.test(s.location.pathname),
  });
  const go = (seatId: string) =>
    void navigate({
      to: "/c/$slug/kpis/scorecard",
      params,
      search: (prev: Record<string, unknown>) => {
        const keep = onScorecard ? prev : {};
        // The grid shows the seat: a Reports or Published tab gives way to it.
        const { kpi: _k, view: _v, ...rest } = keep as Record<string, unknown>;
        return { ...rest, ...keepFocus(prev), s: `seat:${seatId}` };
      },
    } as never);

  if (!chart.data || !tree) return null;
  // Always the server's last answer: a failed refetch keeps the cached set, and a structural seat
  // (KPIs not shared) never becomes a link.
  const structural = tree.structural;

  // WORKFORCE: an agent (AG) hangs under the seat its DE or person holds.
  const agRow = (a: WorkforceAgentRow, depth: number): ReactNode => (
    <li key={`ag:${a.id}`} data-ag={a.id}>
      <Link
        to="/c/$slug/agents/ag/$id"
        params={{ ...params, id: a.id }}
        search={keepFocus}
        title={`${workerName(a.name)} · reports to ${workerName(a.reports_to.name)}`}
        className={`${indent(depth)} ${ROW} focus-visible:outline-2 focus-visible:outline-ring`}
      >
        <span aria-hidden="true" className="size-5 shrink-0" />
        <span className="flex min-w-0 flex-1 flex-col py-1 leading-tight">
          <span className="truncate">{workerName(a.name)}</span>
          <span className="inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
            <WorkerBadge kind="ag" size="sm" />
            <span className="truncate">{a.status === "active" ? "Agent" : a.status}</span>
          </span>
        </span>
      </Link>
    </li>
  );

  const seatNode = (n: SeatNode<TreeSeat>, depth: number, at: string): ReactNode => {
    const id = n.seat.id;
    const seated = holders.get(id) ?? [];
    const lead = seated[0];
    const de = seated.find((h) => h.kind === "digital_employee");
    const label = seatHolderLabel(
      n.seat.title,
      lead ? seated.map((h) => workerName(h.name)).join(", ") : "Open",
    );
    const expanded = !!open?.has(id);
    const ags = seated.flatMap((h) => agsBy.get(h.membership_id) ?? []);
    const hasKids = n.children.length > 0 || ags.length > 0;
    const shapeOnly = structural.has(id);
    const current = focusedSeat === id && onScorecard;
    return (
      <li key={`${at}:${id}`} data-seat={id} data-expanded={hasKids ? expanded : undefined}>
        <div data-current={current ? "true" : undefined} className={`${indent(depth)} ${ROW}`}>
          <Toggle label={label} has={hasKids} open={expanded} onToggle={() => toggle(id)} />
          <button
            type="button"
            title={shapeOnly ? `${label} · KPIs not shared with you` : label}
            data-structural={shapeOnly ? "true" : undefined}
            aria-current={current ? "page" : undefined}
            // A structural seat (in the chain, KPIs hidden) opens or closes; it never links.
            onClick={() => (shapeOnly ? hasKids && toggle(id) : go(id))}
            onKeyDown={keys(id, hasKids, expanded)}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-sm py-1 text-left focus-visible:outline-2 focus-visible:outline-ring"
          >
            <AvatarTile
              name={lead ? workerName(lead.name) : n.seat.title}
              kind={lead?.kind === "digital_employee" ? "agent" : "person"}
              size="xs"
            />
            {/* WORKFORCE: the seat title, then its holder(s), badged, in smaller text. */}
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span
                className={`truncate ${shapeOnly ? "text-muted-foreground" : ""}`}
                data-slot="seat-title"
              >
                {n.seat.title}
              </span>
              <span
                className="inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground"
                data-slot="seat-holder"
              >
                {seated.length ? (
                  seated.map((h) => (
                    <span key={h.membership_id} className="inline-flex min-w-0 items-center gap-1">
                      <WorkerBadge kind={holderKind(h.kind)} size="sm" />
                      <span className="truncate">{workerName(h.name)}</span>
                    </span>
                  ))
                ) : (
                  <span className="italic">Open</span>
                )}
              </span>
            </span>
          </button>
          {de ? (
            <Link
              to="/c/$slug/agents/de/$id"
              params={{ slug: company.slug, id: de.membership_id }}
              search={keepFocus}
              aria-label={`Open ${workerName(de.name)}`}
              title={`Open ${workerName(de.name)}`}
              data-slot="seat-open-de"
              className="flex size-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground opacity-0 group-hover/nav-row:opacity-100 hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-ring pointer-coarse:opacity-100"
            >
              <BotIcon aria-hidden="true" className="size-3.5" />
            </Link>
          ) : null}
        </div>
        {hasKids && expanded ? (
          <ul className="flex flex-col gap-px">
            {ags.map((a) => agRow(a, depth + 1))}
            {n.children.map((c) => seatNode(c, depth + 1, at))}
          </ul>
        ) : null}
      </li>
    );
  };

  /** A scope row (Whole company or a team): the link sets the scope, the chevron opens it. */
  const scopeRow = (
    key: string,
    label: string,
    icon: typeof UsersIcon,
    scope: string | null,
    current: boolean,
    has: boolean,
    depth: number,
    kids: () => ReactNode[],
    team?: string,
  ): ReactNode => {
    const Icon = icon;
    const expanded = !!open?.has(key);
    return (
      <li key={key} data-team={team} data-expanded={has ? expanded : undefined}>
        <div data-current={current ? "true" : undefined} className={`${indent(depth)} ${ROW}`}>
          <Toggle label={label} has={has} open={expanded} onToggle={() => toggle(key)} />
          <Link
            to={to}
            params={params}
            search={scoped(scope) as never}
            aria-current={current ? "page" : undefined}
            onKeyDown={keys(key, has, expanded)}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-sm py-1 focus-visible:outline-2 focus-visible:outline-ring"
          >
            <Icon aria-hidden="true" className="size-4 shrink-0 text-faint" />
            <span className="truncate">{label}</span>
          </Link>
        </div>
        {has && expanded ? <ul className="flex flex-col gap-px">{kids()}</ul> : null}
      </li>
    );
  };

  const teamNode = (t: ScopeTeam<TreeSeat>, depth: number): ReactNode =>
    scopeRow(
      teamKey(t.team.id),
      t.team.name,
      UsersIcon,
      `team:${t.team.id}`,
      focus.scope === `team:${t.team.id}`,
      t.teams.length > 0 || t.seats.length > 0,
      depth,
      () => [
        ...t.teams.map((c) => teamNode(c, depth + 1)),
        ...t.seats.map((s) => seatNode(s, depth + 1, t.team.id)),
      ],
      t.team.id,
    );

  const empty = !tree.teams.length && !tree.rest.length;
  return (
    <>
      <SidebarCaption label="Scope" />
      <ul aria-label="Teams and seats" data-slot="kpis-scope-tree" className="flex flex-col gap-px">
        {scopeRow(
          COMPANY,
          "Whole company",
          Building2Icon,
          null,
          focus.scope === "company",
          !empty,
          0,
          () => [
            ...tree.teams.map((t) => teamNode(t, 1)),
            ...tree.rest.map((s) => seatNode(s, 1, "company")),
          ],
        )}
      </ul>
      {empty && visibility.isSuccess && !chart.data.teams.length ? (
        <SidebarEmpty things="teams or seats" />
      ) : null}
    </>
  );
}
